import { Placeholder } from "@tiptap/extensions";
import { Markdown } from "@tiptap/markdown";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { FC } from "react";
import { TbDeviceFloppy, TbSparkles } from "react-icons/tb";
import { useEventCallback } from "usehooks-ts";
import { MarkdownEditor } from "../common/markdown/MarkdownEditor";
import { useModal } from "../common/modal/Modal";
import {
	useMarketingPlan,
	useMarketingPlanActions,
} from "../company/useMarketingPlan";
import { Button } from "../components/Button";
import { Card } from "../components/Card";

export const TabMarketingPlan: FC = () => {
	const plan = useMarketingPlan();
	const { generatePlan, save } = useMarketingPlanActions();
	const editor = useEditor({
		extensions: [
			StarterKit,
			Markdown,
			Placeholder.configure({ placeholder: "Write something..." }),
		],
		content: plan || undefined,
		contentType: "markdown",
	});
	const { confirm, close } = useModal();

	const handleGenerate = () => {
		const onConfirm = async () => {
			close();
			const newPlan = await generatePlan.mutateAsync();
			editor.commands.setContent(newPlan, { contentType: "markdown" });
		};

		if (!plan) {
			return onConfirm();
		}

		return confirm({
			children: (
				<>
					Are you sure you want to generate a new marketing plan?
					<br />
					<strong>This action will override your existing plan.</strong>
				</>
			),
			confirmText: "Generate",
			onConfirm,
		});
	};

	const handleSave = useEventCallback(() => {
		save.mutate(editor.getMarkdown());
	});

	return (
		<div>
			<Card className="mb-3">
				<Card.Header>
					Your Marketing Plan
					<Button
						size="xsm"
						icon={TbSparkles}
						loading={generatePlan.isPending}
						onClick={handleGenerate}
					>
						Generate
					</Button>
				</Card.Header>
				<Card.Body>
					<MarkdownEditor editor={editor} />
				</Card.Body>
				<Card.Footer style={{ justifyContent: "end" }}>
					<Button
						icon={TbDeviceFloppy}
						size="sm"
						onClick={handleSave}
						loading={save.isPending}
					>
						Save
					</Button>
				</Card.Footer>
			</Card>
		</div>
	);
};
